import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore, selectBalance, selectTotalIncome, selectTotalExpenses, selectUser, selectIncomeUnitSummaries, selectMembers } from '@/utils/store';
import { useCurrency } from '@/hooks/useCurrency';
import { formatCurrency as formatCurrencyRaw } from '@/utils/format';
import TransactionItem from '@/components/shared/TransactionItem';
import AddTransactionModal from '@/components/shared/AddTransactionModal';
import AddIncomeUnitModal from '@/components/shared/AddIncomeUnitModal';
import FloatingActionButton from '@/components/shared/FloatingActionButton';
import MonthlyOverviewChart from '@/components/app/MonthlyOverviewChart';
import ExpenseBreakdownChart from '@/components/app/ExpenseBreakdownChart';
import WeeklyTrendChart from '@/components/app/WeeklyTrendChart';
import InfoTooltip from '@/components/ui/InfoTooltip';
import ExchangeRateBadge from '@/components/shared/ExchangeRateBadge';
import { TrendingUp, TrendingDown, Wallet, Plus, Trash2 } from 'lucide-react';

export default function Dashboard() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const user = useStore(selectUser);
  const balance = useStore(selectBalance);
  const income = useStore(selectTotalIncome);
  const expenses = useStore(selectTotalExpenses);
  const transactions = useStore((s) => s.transactions.slice(0, 5));
  const unitSummaries = useStore(selectIncomeUnitSummaries);
  const members = useStore(selectMembers);
  const removeIncomeUnit = useStore((s) => s.removeIncomeUnit);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('expense');
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setShowUnitModal(true);
  };

  const closeUnitModal = () => {
    setShowUnitModal(false);
    setEditingUnit(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white">
        {t('dashboard.greeting', { name: user.name })} 👋
      </h1>

      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-brand-500 to-brand-700 text-white border-0">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-1">
            <Wallet size={16} />
            {t('dashboard.balance')}
            <InfoTooltip text={t('common.hints.balance')} size={13} />
          </div>
          <div className="text-2xl font-bold">{formatCurrency(balance)}</div>
          <div className="text-xs text-white/60 mt-1">{t('dashboard.thisMonth')}</div>
        </div>
        <div className="card">
          <div className="flex items-center gap-2 text-success-500 text-sm mb-1">
            <TrendingUp size={16} />
            {t('dashboard.income')}
            <InfoTooltip text={t('common.hints.income')} size={13} />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(income)}
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-2 text-danger-500 text-sm mb-1">
            <TrendingDown size={16} />
            {t('dashboard.expenses')}
            <InfoTooltip text={t('common.hints.expense')} size={13} />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(expenses)}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex gap-3">
        <button
          onClick={() => openModal('income')}
          className="flex-1 py-3 rounded-xl text-sm font-semibold bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 hover:bg-success-100 dark:hover:bg-success-500/20 transition-colors"
          aria-label={t('transactions.form.addIncome')}
        >
          + {t('dashboard.quickIncome')}
        </button>
        <button
          onClick={() => openModal('expense')}
          className="flex-1 py-3 rounded-xl text-sm font-semibold bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 hover:bg-danger-100 dark:hover:bg-danger-500/20 transition-colors"
          aria-label={t('transactions.form.addExpense')}
        >
          - {t('dashboard.quickExpense')}
        </button>
      </div>

      {/* Income Units */}
      {unitSummaries.length > 0 && (
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {t('incomeUnits.title')}
              </h2>
              <InfoTooltip text={t('common.hints.incomeUnits')} size={14} />
            </div>
            <button
              onClick={() => setShowUnitModal(true)}
              className="flex items-center gap-1 text-sm text-brand-600 dark:text-brand-400 font-medium hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
            >
              <Plus size={16} />
              {t('incomeUnits.addNew')}
            </button>
          </div>
          <div className="space-y-4">
            {unitSummaries.map(({ unit, totalFixed, totalVariable, netIncome }) => {
              const ratio = unit.grossIncome > 0 ? Math.round((netIncome / unit.grossIncome) * 100) : 0;
              const unitMembers = unit.memberIds?.length > 0 ? unit.memberIds.map((id) => members.find((m) => m.id === id)).filter(Boolean) : [];
              return (
                <div
                  key={unit.id}
                  className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group"
                  onClick={() => openEditUnit(unit)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{unit.icon}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{unit.name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                        {unit.currency}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {unitMembers.length > 0 ? (
                        <div className="flex -space-x-1">
                          {unitMembers.map((m) => (
                            <span
                              key={m.id}
                              className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white ring-2 ring-white dark:ring-gray-900"
                              style={{ backgroundColor: m.color }}
                            >
                              {m.emoji || m.initials}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">{t('incomeUnits.shared')}</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); removeIncomeUnit(unit.id); }}
                        className="p-1 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-danger-500 dark:hover:text-danger-400 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    {t('incomeUnits.grossIncome')}: <span className="font-medium text-gray-700 dark:text-gray-300">{formatCurrencyRaw(unit.grossIncome, unit.currency)}</span>
                    <span className="mx-2">|</span>
                    {t('incomeUnits.fixedCosts')}: <span className="font-medium text-danger-600 dark:text-danger-400">-{formatCurrencyRaw(totalFixed, unit.currency)}</span>
                    <span className="mx-2">|</span>
                    {t('incomeUnits.variableCosts')}: <span className="font-medium text-danger-600 dark:text-danger-400">-{formatCurrencyRaw(totalVariable, unit.currency)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all ${
                          ratio > 70 ? 'bg-success-500' : ratio > 50 ? 'bg-warning-500' : 'bg-danger-500'
                        }`}
                        style={{ width: `${Math.max(0, Math.min(100, ratio))}%` }}
                      />
                    </div>
                    <span className={`text-sm font-bold ${
                      ratio > 70 ? 'text-success-600 dark:text-success-400' : ratio > 50 ? 'text-warning-600 dark:text-warning-400' : 'text-danger-600 dark:text-danger-400'
                    }`}>
                      {formatCurrencyRaw(netIncome, unit.currency)}
                      {unit.currency !== (user.currency || 'CLP') && (
                        <ExchangeRateBadge amount={netIncome} from={unit.currency} to={user.currency || 'CLP'} />
                      )}
                    </span>
                    <span className="text-xs text-gray-400">{ratio}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {unitSummaries.length === 0 && (
        <div className="card text-center py-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t('incomeUnits.title')}</p>
          <button
            onClick={() => setShowUnitModal(true)}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
          >
            <Plus size={16} />
            {t('incomeUnits.addNew')}
          </button>
        </div>
      )}

      {/* Charts */}
      <MonthlyOverviewChart />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ExpenseBreakdownChart />
        <WeeklyTrendChart />
      </div>

      {/* Recent transactions */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {t('dashboard.recentTransactions')}
          </h2>
          <button
            onClick={() => navigate('/app/transactions')}
            className="text-sm text-brand-600 dark:text-brand-400 font-medium"
          >
            {t('dashboard.viewAll')}
          </button>
        </div>
        <div className="space-y-3">
          {transactions.map((tx) => (
            <TransactionItem key={tx.id} transaction={tx} compact />
          ))}
        </div>
      </div>

      <FloatingActionButton onClick={() => openModal('expense')} />
      <AddTransactionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        defaultType={modalType}
      />
      <AddIncomeUnitModal
        open={showUnitModal}
        onClose={closeUnitModal}
        unit={editingUnit}
      />
    </div>
  );
}
